import { ContactForm } from "@/components/ContactForm";
import { QuoteForm } from "@/components/QuoteForm";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  MessageCircle,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const ContactPage = () => {
  const contactInfo = [
    {
      icon: Phone,
      title: "Phone",
      value: "+1-771-1871-987",
      link: "tel:+1-771-1871-987",
      desc: "Call us anytime",
    },
    {
      icon: Mail,
      title: "Email",
      value: "support@nexivas.store",
      link: "mailto:support@nexivas.store",
      desc: "We'll respond within 24 hours",
    },
    {
      icon: MapPin,
      title: "Address",
      value: "667 Magnolia Court, Houston, TX 57002",
      link: "https://maps.google.com/?q=667+Magnolia+Court+Houston+TX",
      desc: "Visit our office",
    },
    {
      icon: Clock,
      title: "Business Hours",
      value: "Mon-Fri: 7AM-6PM | Sat: 8AM-4PM",
      link: null,
      desc: "Sunday: Closed",
    },
  ];

  return (
    <div className="pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="bg-forest-950 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-emerald-400 font-accent font-medium uppercase tracking-wider text-sm">Contact Us</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white mt-3 mb-6">
              Get in Touch
            </h1>
            <p className="text-emerald-100/80 text-lg">
              Ready for a greener lawn? Contact us today for a free quote or any questions about our services.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-heading font-bold text-forest-950 mb-6">Contact Information</h2>
              
              <div className="space-y-6">
                {contactInfo.map((item, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-4"
                    data-testid={`contact-info-${index}`}
                  >
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-forest-950 mb-1">{item.title}</h3>
                      {item.link ? (
                        <a 
                          href={item.link}
                          className="text-emerald-600 hover:text-emerald-700 transition-colors"
                          target={item.link.startsWith('http') ? '_blank' : undefined}
                          rel={item.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-gray-700">{item.value}</p>
                      )}
                      <p className="text-gray-500 text-sm mt-1">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Map */}
              <div className="mt-8 map-container h-64 rounded-2xl overflow-hidden">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3464.2539073621444!2d-95.36978992419648!3d29.760192575002954!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8640b8b4488d8501%3A0xca0d02def365053b!2sHouston%2C%20TX!5e0!3m2!1sen!2sus!4v1699999999999!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Nexivas Location"
                ></iframe>
              </div>
            </div>

            {/* Forms */}
            <div className="lg:col-span-2">
              <div className="bg-emerald-50/50 rounded-2xl p-6 sm:p-8">
                <Tabs defaultValue="quote" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-8 bg-emerald-100/50 p-1 rounded-lg">
                    <TabsTrigger 
                      value="quote"
                      className="data-[state=active]:bg-white data-[state=active]:text-emerald-600 rounded-md py-3"
                      data-testid="quote-tab"
                    >
                      Request a Quote
                    </TabsTrigger>
                    <TabsTrigger 
                      value="contact"
                      className="data-[state=active]:bg-white data-[state=active]:text-emerald-600 rounded-md py-3"
                      data-testid="contact-tab"
                    >
                      Send a Message
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="quote" data-testid="quote-tab-content">
                    <div className="mb-6">
                      <h3 className="text-2xl font-heading font-bold text-forest-950 mb-2">Get Your Free Quote</h3>
                      <p className="text-gray-600">Fill out the form below and we'll provide a customized quote within 24 hours.</p>
                    </div>
                    <QuoteForm />
                  </TabsContent>
                  
                  <TabsContent value="contact" data-testid="contact-tab-content">
                    <div className="mb-6">
                      <h3 className="text-2xl font-heading font-bold text-forest-950 mb-2">Send Us a Message</h3>
                      <p className="text-gray-600">Have a question? We'd love to hear from you. Send us a message and we'll respond as soon as possible.</p>
                    </div>
                    <ContactForm />
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ CTA */}
      <section className="py-16 bg-emerald-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <MessageCircle className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-forest-950 mb-4">
            Have More Questions?
          </h2>
          <p className="text-gray-600 text-lg mb-6">
            Check out our FAQ section on the homepage for answers to common questions, or give us a call—we're always happy to help!
          </p>
          <a 
            href="tel:+1-771-1871-987" 
            className="inline-flex items-center gap-2 text-emerald-600 font-semibold hover:text-emerald-700 transition-colors text-lg"
            data-testid="contact-faq-phone"
          >
            <Phone className="w-5 h-5" />
            +1-771-1871-987
          </a>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
